import * as React from "react"
import { Upload } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface FileInputProps {
  id?: string
  accept?: string
  /** Name shown next to the button (selected file or existing file) */
  fileName?: string | null
  onChange: (file: File | null) => void
  disabled?: boolean
  invalid?: boolean
  className?: string
  buttonLabel?: string
}

/**
 * File picker with a visible button and Spanish labels. The native
 * <input type="file"> text ("Choose file" / "No file chosen") follows the
 * browser language, not the page, and its button disappears on dark themes.
 */
export function FileInput({
  id,
  accept,
  fileName,
  onChange,
  disabled,
  invalid,
  className,
  buttonLabel = "Seleccionar archivo",
}: FileInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-md border bg-background p-2",
        invalid && "border-destructive",
        className
      )}
    >
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        className="sr-only"
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.files?.[0] ?? null)
          // Allow picking the same file again after removing it
          e.target.value = ""
        }}
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className="shrink-0 border border-border"
      >
        <Upload className="mr-2 size-4" />
        {buttonLabel}
      </Button>
      <span className={cn("min-w-0 truncate text-sm", fileName ? "text-foreground" : "text-muted-foreground")}>
        {fileName || "Ningún archivo seleccionado"}
      </span>
    </div>
  )
}
